import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["competencyItem"]
  
  connect() {
    console.log("Signature approval controller connected")
    this.initializeSignaturePad()
    this.setupFormValidation()
  }
  
  initializeSignaturePad() {
    const canvas = document.getElementById('signature-pad')
    if (!canvas) return
    
    // Set up canvas size
    const ratio = Math.max(window.devicePixelRatio || 1, 1)
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * ratio
    canvas.height = rect.height * ratio
    const ctx = canvas.getContext("2d")
    ctx.scale(ratio, ratio)
    
    this.canvas = canvas
    this.ctx = ctx
    this.isDrawing = false
    this.hasDrawn = false
    this.points = []
    
    // Mouse events
    canvas.addEventListener('mousedown', this.startDrawing.bind(this))
    canvas.addEventListener('mousemove', this.draw.bind(this))
    canvas.addEventListener('mouseup', this.stopDrawing.bind(this))
    canvas.addEventListener('mouseleave', this.stopDrawing.bind(this))
    
    // Touch events for mobile
    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault()
      const touch = e.touches[0]
      const mouseEvent = new MouseEvent('mousedown', {
        clientX: touch.clientX,
        clientY: touch.clientY
      })
      canvas.dispatchEvent(mouseEvent)
    })
    
    canvas.addEventListener('touchmove', (e) => {
      e.preventDefault()
      const touch = e.touches[0]
      const mouseEvent = new MouseEvent('mousemove', {
        clientX: touch.clientX,
        clientY: touch.clientY
      })
      canvas.dispatchEvent(mouseEvent)
    })
    
    canvas.addEventListener('touchend', (e) => {
      e.preventDefault()
      const mouseEvent = new MouseEvent('mouseup', {})
      canvas.dispatchEvent(mouseEvent)
    })
  }
  
  startDrawing(e) {
    this.isDrawing = true
    const rect = this.canvas.getBoundingClientRect()
    this.ctx.beginPath()
    this.ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top)
  }
  
  draw(e) {
    if (!this.isDrawing) return
    
    const rect = this.canvas.getBoundingClientRect()
    this.ctx.lineWidth = 2
    this.ctx.lineCap = 'round'
    this.ctx.strokeStyle = '#000'
    this.ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top)
    this.ctx.stroke()
    this.hasDrawn = true
  }
  
  stopDrawing() {
    if (this.isDrawing) {
      this.isDrawing = false
      this.validateForm()
    }
  }
  
  agree(event) {
    const item = event.target.closest('.competency-item')
    const candidateLevel = item.dataset.candidateLevel
    const successLevel = item.querySelector('.preceptor-success-level')
    const approved = item.querySelector('.approved-by-preceptor')
    
    // Set to candidate's level
    successLevel.value = candidateLevel
    approved.value = 'true'
    
    this.updateItemDisplay(item, 'agreed', candidateLevel)
    this.validateForm()
  }
  
  disagree(event) {
    const item = event.target.closest('.competency-item')
    const disagreeOptions = item.querySelector('.disagree-options')
    const approvalButtons = item.querySelector('.approval-buttons')
    
    // Hide buttons, show level selection
    approvalButtons.style.display = 'none'
    disagreeOptions.style.display = 'block'
  }
  
  selectLevel(event) {
    const level = event.target.dataset.level
    const item = event.target.closest('.competency-item')
    const successLevel = item.querySelector('.preceptor-success-level')
    const approved = item.querySelector('.approved-by-preceptor')
    
    successLevel.value = level
    approved.value = 'true'
    
    this.updateItemDisplay(item, 'disagreed', level)
    this.validateForm()
  }
  
  undo(event) {
    const item = event.target.closest('.competency-item')
    const successLevel = item.querySelector('.preceptor-success-level')
    const approved = item.querySelector('.approved-by-preceptor')
    
    // Reset
    successLevel.value = ''
    approved.value = 'false'
    
    // Show buttons again
    item.querySelector('.approval-buttons').style.display = 'block'
    item.querySelector('.disagree-options').style.display = 'none'
    item.querySelector('.selection-display').style.display = 'none'
    item.style.backgroundColor = ''
    
    this.validateForm()
  }
  
  updateItemDisplay(item, action, level) {
    const selectionDisplay = item.querySelector('.selection-display')
    const selectionText = item.querySelector('.selection-text')
    const disagreeOptions = item.querySelector('.disagree-options')
    const approvalButtons = item.querySelector('.approval-buttons')
    
    approvalButtons.style.display = 'none'
    disagreeOptions.style.display = 'none'
    selectionDisplay.style.display = 'block'
    
    if (action === 'agreed') {
      selectionText.innerHTML = `<span class="text-success"><i class="fa fa-check"></i> Agreed (${level})</span>`
      selectionDisplay.querySelector('.alert').className = 'alert alert-success mb-2'
    } else {
      selectionText.innerHTML = `<span class="text-warning"><i class="fa fa-exclamation-triangle"></i> Modified to: ${level}</span>`
      selectionDisplay.querySelector('.alert').className = 'alert alert-warning mb-2'
    }
    
    // Add visual feedback
    item.style.backgroundColor = '#f0f8ff'
  }
  
  clearSignature(event) {
    event.preventDefault()
    const ctx = this.ctx
    const canvas = this.canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    this.hasDrawn = false
    this.validateForm()
  }
  
  validateForm() {
    const submitBtn = document.getElementById('submit-btn')
    const approverName = document.getElementById('approver_name').value.trim()
    const approverCreds = document.getElementById('approver_credentials').value.trim()
    const hasSignature = this.hasDrawn
    
    // Check if at least one competency is approved
    const approvedInputs = Array.from(
      document.querySelectorAll('.approved-by-preceptor')
    )
    const hasApprovals = approvedInputs.some(input => input.value === 'true')
    
    const isValid = approverName && approverCreds && hasSignature && hasApprovals
    
    if (submitBtn) {
      submitBtn.disabled = !isValid
      
      if (!isValid) {
        let missingItems = []
        if (!approverName) missingItems.push('Name')
        if (!approverCreds) missingItems.push('Credentials')
        if (!hasSignature) missingItems.push('Signature')
        if (!hasApprovals) missingItems.push('At least one approval')
        
        submitBtn.title = `Missing: ${missingItems.join(', ')}`
      } else {
        submitBtn.title = 'Click to submit approvals'
      }
    }
  }
  
  setupFormValidation() {
    const form = document.getElementById('competency-approval-form')
    if (!form) return
    
    // Save signature data before submit
    form.addEventListener('submit', (event) => {
      if (this.hasDrawn) {
        const canvas = this.canvas
        document.getElementById('signature_data').value = canvas.toDataURL('image/png')
      } else {
        event.preventDefault()
        alert('Please provide your signature')
      }
    })
  }
}

